/**
 * Media links a transcription can start from instead of an uploaded file.
 *
 * Here rather than in apps/api because the web upload modal hints at the same
 * platform list the batch worker routes on: one definition, so the browser can
 * never promise a platform the worker would send down the wrong downloader.
 */

/** Longest accepted source URL, shared by the public batch API and the web link mode. */
export const MAX_SOURCE_URL_LENGTH = 2048

/**
 * Host suffixes whose URLs are media *pages*, not directly fetchable media
 * files, so the batch worker resolves them with yt-dlp instead of a plain HTTP
 * GET. Match is on the registrable host or any subdomain of it
 * (`endsWith('.' + suffix)`).
 *
 * Deliberately an allowlist of known platforms rather than "anything that isn't
 * a media file": the worker also downloads R2 presigned URLs through the same
 * router, and those MUST stay on the plain-fetch path. An allowlist guarantees a
 * presigned URL is never handed to yt-dlp.
 */
export const EXTRACTION_HOST_SUFFIXES = [
  'youtube.com',
  'youtu.be',
  'vimeo.com',
  'x.com',
  'twitter.com',
  'instagram.com',
  'soundcloud.com',
  // Apple Podcasts episode pages resolve to the RSS enclosure via yt-dlp's
  // ApplePodcasts extractor. Without this the URL would fall through to the
  // direct downloader, which would fetch the HTML page and fail.
  'podcasts.apple.com',
  'tiktok.com',
  'facebook.com',
  'fb.watch',
  'twitch.tv',
  'dailymotion.com',
] as const

export type ExtractionHostSuffix = (typeof EXTRACTION_HOST_SUFFIXES)[number]

/**
 * Whether `sourceUrl`'s host is one of `hostSuffixes` or a subdomain of one. A
 * malformed URL matches nothing, so it falls through to whatever the caller
 * does with a non-platform link.
 */
export function urlMatchesHostSuffixes(
  sourceUrl: string,
  hostSuffixes: readonly string[] = EXTRACTION_HOST_SUFFIXES
): boolean {
  let host: string
  try {
    host = new URL(sourceUrl).hostname.toLowerCase()
  } catch {
    return false
  }
  return hostSuffixes.some((suffix) => host === suffix || host.endsWith(`.${suffix}`))
}
